/**
 * Shows archive destinations whose deleted-item reads and restore writes are not connected yet.
 *
 * Discarded task suggestions are real and stay visible without this switch. Deleted tasks, contacts
 * and records are the shape of the destination still being tested, so production must not advertise
 * empty recovery tools as finished features merely because their shells exist.
 */
export const ENABLE_ARCHIVE_DELETED_VIEWS = import.meta.env.VITE_ENABLE_ARCHIVE_DELETED_VIEWS === 'true';

/**
 * Offers Notes as an action of its own on a plan card, in the drawer's overflow menu.
 *
 * Off, the only note a household writes is the one that rides out with a completion as evidence;
 * the standing note on an occurrence has no way in. Opt-in while the two are being told apart —
 * `note` stays in `allowed_actions` either way, so this hides a control the server would still take.
 */
export const ENABLE_SCHEDULE_TASK_NOTES = import.meta.env.VITE_ENABLE_SCHEDULE_TASK_NOTES === 'true';

/**
 * Makes the note on a dismissal compulsory for every reason, not only "Other".
 *
 * A reason on its own says a card was wrong; only the note says how, and that is the whole value of
 * the review pass while the extraction work is being tuned. So this trades a slower dismissal for an
 * answer worth reading, and it is on in staging where the plan is being gone through in anger.
 *
 * Opt-in, so an unset environment keeps the lighter behaviour: optional everywhere except "Other".
 *
 * Temporary. When there is enough feedback the flag comes out altogether and the rule goes back to
 * what it was — delete it rather than setting the variable to `false`, which would leave a switch
 * nobody is ever going to turn on again.
 */
export const REQUIRE_DISMISS_NOTE = import.meta.env.VITE_REQUIRE_DISMISS_NOTE === 'true';

/**
 * Offers the schedule's in-progress Workspace view as a third option on the view toggle.
 *
 * Governs whether the option can be reached, not which layout is drawn — see
 * `docs/schedule/README.md`, "Two layouts for one screen".
 *
 * Opt-out rather than opt-in, unlike the flags above it, because it hides an option instead of a
 * feature: on, the toggle gains an icon and the schedule can draw the two-pane layout. Opt-in while
 * the layout is unfinished — off, the icon is absent and the schedule is the reading column, which
 * is what a stored Workspace preference falls back to without being overwritten.
 */
export const ENABLE_SCHEDULE_WORKSPACE_VIEW = import.meta.env.VITE_ENABLE_SCHEDULE_WORKSPACE_VIEW === 'true';

/**
 * Offers the schedule's facet filters — the control beside the lenses.
 *
 * Opt-in, so an unset environment does not draw them. They are not wrong, they are unwanted for
 * now: the counts behind them are not trustworthy yet — see `Q-DISPLAY-CATEGORY-FACET-OVERCOUNTS` —
 * and the lenses already narrow the dimension a household reaches for. Kept behind a switch rather
 * than deleted, because the control works and the objection is to showing it today.
 */
export const ENABLE_SCHEDULE_FACET_FILTERS = import.meta.env.VITE_ENABLE_SCHEDULE_FACET_FILTERS === 'true';

/**
 * Draws the keyboard legend under a proposal's verdict buttons.
 *
 * Opt-in for the same reason: it earns its place for somebody working through forty proposals with
 * a keyboard and is noise to everyone else, on the densest part of a card. The shortcuts keep
 * working either way — this hides the legend, not the keys.
 */
export const ENABLE_REVIEW_SHORTCUT_HINTS = import.meta.env.VITE_ENABLE_REVIEW_SHORTCUT_HINTS === 'true';

/**
 * Names the provider who can action a warranty. Only `WarrantyTaskDetail` reads it, and that is a
 * live surface: `ScheduleContent` opens it for every warranty card, in place of the task drawer. An
 * earlier note here said nothing rendered it and that the flag was kept only so the file compiled —
 * both were wrong, and acting on either would have taken a working panel out with them.
 */
export const ENABLE_MAINTENANCE_PROVIDER_SEGMENT = import.meta.env.VITE_ENABLE_MAINTENANCE_PROVIDER_SEGMENT === 'true';

/**
 * QR login is hidden while the scanner flow is unfinished; keep it lazy so disabled scanner deps stay
 * out of the common login path.
 */
export const ENABLE_LOGIN_QR_SCANNER_FEATURE = import.meta.env.VITE_ENABLE_LOGIN_QR_SCANNER_FEATURE === 'true';

/**
 * The corrections list reads /houses/{id}/corrections and renders it as-is, to see the shape the
 * backend returns before any of it is designed. Hidden until that design exists.
 */
export const ENABLE_CORRECTIONS_DISPLAY_FEATURE = import.meta.env.VITE_ENABLE_CORRECTIONS_DISPLAY_FEATURE === 'true';
